import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Mic, Square, Loader2, PhoneCall, PhoneOff, Mail, User, Sparkles, MessageSquare } from "lucide-react";
import { Waveform } from "./Waveform";
import { getSessionId, getStoredVisitor, storeVisitor } from "./LiveDemoWidget";
import { PBX_API_ORIGIN } from "@/lib/apiConfig";

// Icon + a small "AI" sparkle badge, so every persona's icon reads as
// "this industry's work, done by AI" rather than just a generic industry icon.
// Springs in on scroll, same pop-in used for the numbered feature icons
// elsewhere on the site.
export function PersonaIcon({ icon: Icon, size = 26 }) {
  return (
    <motion.span
      initial={{ scale: 0, rotate: -20 }}
      whileInView={{ scale: 1, rotate: 0 }}
      viewport={{ once: true }}
      transition={{ type: "spring", stiffness: 260, damping: 18 }}
      className="relative inline-flex"
    >
      <Icon className="text-[#5B8CFF]" size={size} />
      <span className="absolute -right-1.5 -bottom-1.5 grid place-items-center w-4 h-4 rounded-full bg-[#5B8CFF] ring-2 ring-[#0a0a0a]">
        <Sparkles size={9} className="text-white" />
      </span>
    </motion.span>
  );
}

const SPEECH_LEVEL = 16;
const SILENCE_LEVEL = 10;
const SILENCE_HANGOVER_MS = 650;
const MAX_RECORD_MS = 20000;

// India-time-aware opening salutation, so the agent greets with "Good
// morning/afternoon/evening" matching the actual time in India regardless
// of the visitor's own timezone.
function istGreeting() {
  const hour = Number(
    new Intl.DateTimeFormat("en-US", { hour: "numeric", hour12: false, timeZone: "Asia/Kolkata" }).format(new Date())
  );
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

export function AgentVoiceCard({ agentId, label, greeting }) {
  const fullGreeting = `${istGreeting()}, ${greeting}`;
  const sessionId = getSessionId(agentId);
  const [stage, setStage] = useState("idle"); // idle | gate | greeting | recording | processing | speaking | error
  const [visitor, setVisitorState] = useState(() => getStoredVisitor());
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [touched, setTouched] = useState(false);
  const [lastReply, setLastReply] = useState("");
  const [error, setError] = useState("");

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const audioRef = useRef(null);
  const vadRafRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const maxRecordTimerRef = useRef(null);
  const hasSpokenRef = useRef(false);
  // Bumped every time the call starts or ends. Any in-flight async step
  // (a fetch, an await on getUserMedia, audio playback finishing) checks
  // this before acting — if it's stale, it no-ops instead of reviving a
  // call the visitor already hung up on.
  const callIdRef = useRef(0);

  useEffect(() => () => {
    stopVad();
    mediaRecorderRef.current?.stream?.getTracks().forEach((t) => t.stop());
    audioRef.current?.pause();
    window.speechSynthesis?.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ends the call: stops any in-progress recording without letting it
  // auto-send, stops playback, cancels speech synthesis, and resets back
  // to the idle "Talk to this agent" button.
  const hangUp = () => {
    callIdRef.current += 1;
    stopVad();
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.ondataavailable = null;
      if (mediaRecorderRef.current.state !== "inactive") {
        try { mediaRecorderRef.current.stop(); } catch {}
      }
      mediaRecorderRef.current.stream?.getTracks().forEach((t) => t.stop());
      mediaRecorderRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current.pause();
      audioRef.current = null;
    }
    window.speechSynthesis?.cancel();
    setStage("idle");
    setLastReply("");
    setError("");
  };

  const stopVad = () => {
    if (vadRafRef.current) cancelAnimationFrame(vadRafRef.current);
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    if (maxRecordTimerRef.current) clearTimeout(maxRecordTimerRef.current);
    vadRafRef.current = null;
    silenceTimerRef.current = null;
    maxRecordTimerRef.current = null;
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
  };

  const startVad = (stream) => {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    source.connect(analyser);
    audioContextRef.current = ctx;
    hasSpokenRef.current = false;

    const data = new Uint8Array(analyser.frequencyBinCount);
    const tick = () => {
      analyser.getByteFrequencyData(data);
      const avg = data.reduce((a, b) => a + b, 0) / data.length;
      if (avg > SPEECH_LEVEL) {
        hasSpokenRef.current = true;
        if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null; }
      } else if (hasSpokenRef.current && avg <= SILENCE_LEVEL && !silenceTimerRef.current) {
        silenceTimerRef.current = setTimeout(() => stopRecording(), SILENCE_HANGOVER_MS);
      }
      vadRafRef.current = requestAnimationFrame(tick);
    };
    tick();
  };

  const startRecording = async (v) => {
    const myCallId = callIdRef.current;
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (myCallId !== callIdRef.current) { stream.getTracks().forEach((t) => t.stop()); return; }
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => {
        stopVad();
        stream.getTracks().forEach((t) => t.stop());
        if (myCallId !== callIdRef.current) return;
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        sendVoice(blob, v, myCallId);
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setStage("recording");
      startVad(stream);
      maxRecordTimerRef.current = setTimeout(() => stopRecording(), MAX_RECORD_MS);
    } catch {
      if (myCallId !== callIdRef.current) return;
      setStage("error");
      setError("Microphone access denied — allow mic access to talk to this agent.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
      setStage("processing");
    }
  };

  const sendVoice = async (blob, v, myCallId) => {
    try {
      const res = await fetch(`${PBX_API_ORIGIN}/api/public-demo/voice`, {
        method: "POST",
        headers: {
          "X-Session-Id": sessionId,
          "X-Visitor": encodeURIComponent(JSON.stringify(v)),
          "X-Page-Url": encodeURIComponent(window.location.href),
          "X-Agent-Id": agentId,
        },
        body: blob,
      });
      if (myCallId !== callIdRef.current) return;
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Something went wrong");
      }
      const aiText = decodeURIComponent(res.headers.get("X-Ai-Text") || "");
      const audioBlob = await res.blob();
      if (myCallId !== callIdRef.current) return;
      setLastReply(aiText);
      setStage("speaking");
      const audioEl = new Audio(URL.createObjectURL(audioBlob));
      audioRef.current = audioEl;
      audioEl.onended = () => { if (myCallId === callIdRef.current) startRecording(v); };
      audioEl.onerror = () => { if (myCallId === callIdRef.current) setStage("error"); };
      audioEl.play().catch(() => { if (myCallId === callIdRef.current) setStage("error"); });
    } catch (e) {
      if (myCallId !== callIdRef.current) return;
      setStage("error");
      setError(e.message || "Couldn't process that — please try again.");
    }
  };

  // The greeting is voiced through the same real TTS pipeline as every
  // other reply (Sarvam Bulbul for Hindi/Indian languages, ElevenLabs
  // otherwise) via /speak, instead of the browser's low-quality built-in
  // speech synthesis — falls back to that only if the network call fails.
  const playGreeting = async (v) => {
    const myCallId = callIdRef.current;
    setStage("greeting");
    setLastReply(fullGreeting);
    try {
      const res = await fetch(`${PBX_API_ORIGIN}/api/public-demo/speak`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: fullGreeting, agent_id: agentId }),
      });
      if (myCallId !== callIdRef.current) return;
      if (!res.ok) throw new Error("speak failed");
      const blob = await res.blob();
      if (myCallId !== callIdRef.current) return;
      const audioEl = new Audio(URL.createObjectURL(blob));
      audioRef.current = audioEl;
      audioEl.onended = () => { if (myCallId === callIdRef.current) startRecording(v); };
      audioEl.onerror = () => { if (myCallId === callIdRef.current) startRecording(v); };
      await audioEl.play();
    } catch {
      if (myCallId !== callIdRef.current) return;
      const utter = new SpeechSynthesisUtterance(fullGreeting);
      utter.rate = 1.02;
      utter.onend = () => { if (myCallId === callIdRef.current) startRecording(v); };
      utter.onerror = () => { if (myCallId === callIdRef.current) startRecording(v); };
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
        window.speechSynthesis.speak(utter);
      } else {
        startRecording(v);
      }
    }
  };

  const begin = () => {
    if (visitor) { playGreeting(visitor); return; }
    setStage("gate");
  };

  const submitGate = (e) => {
    e.preventDefault();
    setTouched(true);
    if (!name.trim() || !phone.trim() || !consent) return;
    const v = { name: name.trim(), phone: phone.trim(), email: email.trim() || null };
    storeVisitor(v);
    setVisitorState(v);
    playGreeting(v);
  };

  const listening = stage === "recording";
  const speaking = stage === "greeting" || stage === "speaking";

  if (stage === "idle") {
    return (
      <button
        onClick={begin}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-neutral-900 font-semibold text-sm py-2.5 hover:bg-neutral-200 transition-colors"
        data-testid={`agent-demo-cta-${agentId}`}
      >
        <MessageSquare size={15} /> Talk to this agent
      </button>
    );
  }

  if (stage === "gate") {
    return (
      <form onSubmit={submitGate} className="space-y-2.5">
        <div className="relative">
          <User size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
            className="w-full pl-8 pr-3 py-2 bg-white/5 text-white text-[12px] border border-white/10 focus:outline-none focus:border-[#5B8CFF] placeholder:text-neutral-500" />
        </div>
        <div className="relative">
          <PhoneCall size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone number" type="tel"
            className="w-full pl-8 pr-3 py-2 bg-white/5 text-white text-[12px] border border-white/10 focus:outline-none focus:border-[#5B8CFF] placeholder:text-neutral-500" />
        </div>
        <div className="relative">
          <Mail size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (optional)" type="email"
            className="w-full pl-8 pr-3 py-2 bg-white/5 text-white text-[12px] border border-white/10 focus:outline-none focus:border-[#5B8CFF] placeholder:text-neutral-500" />
        </div>
        {touched && (!name.trim() || !phone.trim()) && <p className="text-[10px] text-red-400">Name and phone are required.</p>}
        <label className="flex items-start gap-2 pt-0.5">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 shrink-0 accent-[#5B8CFF]" />
          <span className="text-[10px] leading-relaxed text-neutral-500">
            I agree my details and this voice conversation will be saved, per the{" "}
            <a href="/privacy" target="_blank" rel="noreferrer" className="underline hover:text-white">Privacy Policy</a>.
          </span>
        </label>
        {touched && name.trim() && phone.trim() && !consent && <p className="text-[10px] text-red-400">Please accept to continue.</p>}
        <button type="submit" className="w-full mt-1 rounded-full bg-white text-neutral-900 font-semibold text-[12px] py-2 hover:bg-neutral-200 transition-colors">
          Start talking
        </button>
      </form>
    );
  }

  return (
    <div data-testid={`agent-voice-${agentId}`}>
      <Waveform bars={28} height={40} color={speaking ? "#5B8CFF" : listening ? "#ffffff" : "#3f3f46"} active={listening || speaking} />
      {lastReply && (
        <p className="mt-2 text-[11px] text-neutral-400 leading-relaxed line-clamp-3">{lastReply}</p>
      )}
      {error && <p className="mt-2 text-[11px] text-red-400">{error}</p>}
      <div className="mt-3 flex items-center justify-center gap-3">
        <button
          onClick={listening ? stopRecording : stage === "error" ? () => startRecording(visitor) : undefined}
          disabled={stage === "processing" || speaking}
          className={`grid place-items-center w-11 h-11 rounded-full transition-colors ${
            listening ? "bg-red-600 text-white" : "bg-white text-neutral-900 disabled:opacity-40"
          }`}
          aria-label={listening ? "Stop" : "Talk"}
        >
          {stage === "processing" || speaking ? <Loader2 size={16} className="animate-spin" /> : listening ? <Square size={14} fill="currentColor" /> : <Mic size={16} />}
        </button>
        <button
          onClick={hangUp}
          className="grid place-items-center w-11 h-11 rounded-full bg-red-600/15 text-red-500 hover:bg-red-600 hover:text-white transition-colors"
          aria-label="End call"
          data-testid={`agent-hangup-${agentId}`}
        >
          <PhoneOff size={16} />
        </button>
      </div>
      <div className="mt-2 text-center">
        <span className="font-mono-t text-[10px] uppercase tracking-widest text-neutral-500">
          {listening ? "Listening…" : stage === "processing" ? "Thinking…" : speaking ? "Speaking…" : "Tap to talk"}
        </span>
      </div>
    </div>
  );
}
